import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Cookie from 'js-cookie';

import api from '../api';
import userApi from '../../users/api';
import localStore from '../../../utils/localStorage';
import { User } from '../../users/types';
import { Auth, LoginParams, type ResetPasswordBody } from '../helpers/helpers';
import SetPasswordDialog from '../../../routes/Login/components/SetPasswordDialog';

import useIdleTimeout from '../../../hooks/useIdleTimeout';
import AuthContext from './context';
import type { ForgotPasswordBody, ReasonType } from '../helpers/helpers';

function AuthProvider({ children }: React.PropsWithChildren) {
    const history = useHistory();
    const [session, setSession] = useState<string>(global.document ? Cookie.get('user') || '' : '');
    const [user, setUser] = useState<Array<User>>([]);
    const currUser: Auth['currUser'] = useMemo(() => user[0] || null, [user]);
    const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
    const [passwordDialogReason, setPasswordDialogReason] = useState<ReasonType>('no_password');

    const register = useCallback(
        (body: any) =>
            api
                .register(body)
                .then(() => {
                    const sess = Cookie.get('user') || '';
                    setSession(sess);
                    return userApi
                        .getById(sess)
                        .then((currentUser) => setUser([currentUser]))
                        .then(() => history.push('/onboarding/step2'));
                })
                .catch((err) => {
                    console.error('err', err);
                }),
        [history]
    );

    const completeLogin = useCallback(
        async (userId: string, redirect: string) => {
            const sess = userId || Cookie.get('user') || '';
            setSession(sess);
            const currentUser = await userApi.getById(sess);
            setUser([currentUser]);
            history.push(redirect);
        },
        [history]
    );

    const login = useCallback(
        (params: LoginParams) =>
            api
                .login(params)
                .then(async (data) => {
                    // OTP required — return to LoginForm to show OTP step
                    if (data?.requiresOtp) return data;
                    // First login / expired password — go to dedicated set-password page
                    if (data?.mustSetPassword) {
                        history.push('/set-password', { email: params.email });
                        return data;
                    }
                    await completeLogin(data?.userId?.toString(), params.redirect);
                    return data;
                })
                .catch((err) => {
                    console.error('err', err);
                    throw err;
                }),
        [completeLogin, history]
    );

    const verifyOtp = useCallback(
        async (code: string, redirect: string, rememberDevice?: boolean) => {
            // Only the verify-otp call itself may signal a wrong code — let
            // that error propagate to the OtpStep catch.
            const data = await api.verifyOtp({ code, rememberDevice });
            // OTP accepted server-side (session + cookie are set). Finalize
            // login best-effort; if the immediate user fetch fails (e.g. the
            // new session cookie hasn't been adopted by the browser yet),
            // still set session state + navigate so the auth useEffect
            // re-fetches the user once cookies settle.
            try {
                // await completeLogin(data?.userId?.toString(), redirect);
                // We pass rememberDevice inside the object to the API
                const data = await api.verifyOtp({ code, rememberDevice });

                if (data.session) {
                    setSession(data.session);
                    if (global?.document) {
                        Cookie.set('user', data.session);
                    }
                }
                history.push(redirect || '/');
            } catch (err) {
                console.error('post-OTP completeLogin failed; continuing', err);
                const sess = data?.userId?.toString() || Cookie.get('user') || '';
                setSession(sess);
                history.push(redirect);
            }
            return data;
        },
        [history]
    );

    const openChangePassword = useCallback(async () => {
        setPasswordDialogReason('user_change');
        setPasswordDialogOpen(true);
    }, []);

    const logout = useCallback(
        () =>
            api.logout().then(() => {
                setSession('');
                setUser([]);
            }),
        []
    );

    useEffect(() => {
        if (session) {
            userApi
                .getById(session)
                .then((currentUser) => setUser([currentUser]))
                .catch((err) => {
                    console.warn(
                        '[AUTH] getById failed — clearing session.',
                        'status:',
                        err?.response?.status,
                        'message:',
                        err?.message
                    );
                    // Stale or invalid session — clear cookie so isLoggedIn becomes false
                    Cookie.remove('user');
                    setSession('');
                });
        } else {
            // No cookie — but server session might still be alive (e.g. new tab, expired cookie).
            // Check with server before giving up.
            api.checkSession().then((data: any) => {
                if (data?.authenticated && data?.userId) {
                    setSession(String(data.userId));
                }
            });
        }
    }, [session]);

    useEffect(() => {
        const token = localStore.getItem('rememberMe');
        if (token && token !== 'undefined' && !session) {
            //@ts-ignore
            login({ token, rememberMe: true, redirect: '/' }).catch(() => {
                localStore.removeItem('rememberMe');
            });
        }
    }, [session]); // eslint-disable-line

    const isLoggedIn = useMemo(() => Boolean(session), [session]);

    // Auto sign-out after idle timeout
    useIdleTimeout(() => {
        if (isLoggedIn) {
            console.warn('[AUTH] idle timeout fired — calling logout');
            logout();
        }
    }, isLoggedIn);
    const forgotPassword = useCallback((body: ForgotPasswordBody) => api.forgotPassword(body), []);
    const resetPassword = useCallback((body: ResetPasswordBody) => api.resetPassword(body), []);
    return (
        <AuthContext.Provider
            value={{
                setUser,
                isLoggedIn,
                session,
                logout,
                login,
                register,
                user,
                currUser,
                localStore,
                forgotPassword,
                resetPassword,
                openChangePassword,
                verifyOtp
            }}
        >
            {children}
            <SetPasswordDialog
                open={passwordDialogOpen}
                onClose={() => setPasswordDialogOpen(false)}
                reason={passwordDialogReason}
            />
        </AuthContext.Provider>
    );
}

export default AuthProvider;
